import {
  Undo2,
  Redo2,
  MessageSquareText,
  MessageSquareOff,
  List,
  Download,
  Loader2,
  WifiOff,
  FileText,
  FileType2,
  Printer,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { DocumentStatus } from '@/lib/documents/document-client'

interface DocumentActionsProps {
  status: DocumentStatus
  openCommentCount: number
  /** Resolved threads — shown in the "Visa lösta (N)" menu item. */
  resolvedCount: number
  showResolved: boolean
  /** "Dölj alla kommentarer" is on (the icon shows it). */
  commentsHidden: boolean
  canUndo: boolean
  canRedo: boolean
  exporting: 'pdf' | 'docx' | null
  pdfAvailable: boolean
  onUndo: () => void
  onRedo: () => void
  /** "Alla kommentarer" (the list sheet / drawer); omitted in the margin layout. */
  onOpenList?: () => void
  onToggleResolved: () => void
  onToggleHidden: () => void
  onPrint: () => void
  onExportPdf: () => void
  onExportDocx: () => void
}

/**
 * Document controls mounted in the top bar (PLAN-079 §4.1): undo/redo of
 * this session's edits, comments, print, download (PDF / Word), connection
 * state. Who is online is the
 * project-wide TeamAvatarStack next to the bell; per-block presence is
 * shown in the editor itself.
 */
export function DocumentActions({
  status,
  openCommentCount,
  resolvedCount,
  showResolved,
  commentsHidden,
  canUndo,
  canRedo,
  exporting,
  pdfAvailable,
  onUndo,
  onRedo,
  onOpenList,
  onToggleResolved,
  onToggleHidden,
  onPrint,
  onExportPdf,
  onExportDocx,
}: DocumentActionsProps) {
  const { t } = useTranslation('editor')

  return (
    <TooltipProvider delayDuration={500}>
      <div className="flex items-center gap-0.5">
        {status !== 'connected' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground mr-1 flex items-center">
                {status === 'connecting' ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <WifiOff className="h-3.5 w-3.5" />
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {status === 'connecting' ? t('blocks.connecting') : t('blocks.offline')}
            </TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={!canUndo}
              onClick={onUndo}
              aria-label={t('toolbar.undo')}
            >
              <Undo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('toolbar.undo')}</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={!canRedo}
              onClick={onRedo}
              aria-label={t('toolbar.redo')}
            >
              <Redo2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('toolbar.redo')}</TooltipContent>
        </Tooltip>
        {/* Comments menu (board card z2ex): hide all / show resolved / the list. */}
        <DropdownMenu modal={false}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant={commentsHidden ? 'secondary' : 'ghost'}
                  size="icon"
                  className="relative h-8 w-8"
                  aria-label={commentsHidden ? t('comments.hiddenBadge') : t('comments.title')}
                  data-comments-menu
                  data-comments-hidden={commentsHidden || undefined}
                >
                  {commentsHidden ? (
                    <MessageSquareOff className="text-muted-foreground h-4 w-4" />
                  ) : (
                    <MessageSquareText className="h-4 w-4" />
                  )}
                  {openCommentCount > 0 && (
                    <span
                      className={cn(
                        'absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] leading-none',
                        commentsHidden
                          ? 'bg-muted text-muted-foreground'
                          : 'bg-primary text-primary-foreground'
                      )}
                    >
                      {openCommentCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {commentsHidden ? t('comments.hiddenBadge') : t('comments.title')}
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end" className="w-60">
            <DropdownMenuCheckboxItem
              checked={commentsHidden}
              onCheckedChange={onToggleHidden}
              data-menu-hide-all
            >
              {t('comments.hideAll')}
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={showResolved}
              onCheckedChange={onToggleResolved}
              disabled={commentsHidden}
              data-menu-show-resolved
            >
              {showResolved
                ? t('comments.hideResolved')
                : t('comments.showResolved', { count: resolvedCount })}
            </DropdownMenuCheckboxItem>
            {onOpenList && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onOpenList} data-menu-list>
                  <List className="mr-2 h-4 w-4" />
                  {t('comments.allComments')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onPrint}
              aria-label={t('blocks.print')}
            >
              <Printer className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('blocks.print')}</TooltipContent>
        </Tooltip>
        <DropdownMenu modal={false}>
          <Tooltip>
            <TooltipTrigger asChild>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={exporting !== null}
                  aria-label={t('blocks.download')}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">{t('blocks.download')}</TooltipContent>
          </Tooltip>
          <DropdownMenuContent align="end">
            {pdfAvailable && (
              <DropdownMenuItem onClick={onExportPdf}>
                <FileText className="mr-2 h-4 w-4" />
                {t('blocks.exportPdf')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={onExportDocx}>
              <FileType2 className="mr-2 h-4 w-4" />
              {t('blocks.exportDocx')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  )
}
